import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cx from 'classnames'
import { Input } from 'almanac-ui/TextInputField/Input'
import Avatar from 'common/Avatar/Avatar'
import { useDebounce } from 'hooks/useDebounce'
import { useSearchUsers } from 'hooks/queries/externalIntegrations/useSearchUsers'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { PostTag } from 'types'
import { getInitials } from 'utils'
import Styles from './EditCaptionModal.module.scss'

const MAX_SUGGESTIONS = 20
const MIN_QUERY_LENGTH = 3

const MIRROR_STYLE_PROPS = [
  'boxSizing',
  'width',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'letterSpacing',
  'textTransform',
  'wordSpacing',
  'textIndent',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'lineHeight',
  'overflowWrap',
  'wordWrap',
] as const

const LINKEDIN_MENTION_RE = /@\[([^\]]+)\]\((urn:li:(?:person|organization):[^)]+)\)/g
const FACEBOOK_MENTION_RE = /@\[([^\]]+)\]\((fb:\d+)\)/g

/**
 * Converts stored mention format to clean display text,
 * returning both the display string and a name→ID map for round-tripping.
 */
export function storedToDisplay(stored: string, re: RegExp): { display: string; mentions: Map<string, string> } {
  const mentions = new Map<string, string>()
  const display = stored.replace(re, (_, name: string, id: string) => {
    mentions.set(name, id)
    return `@${name}`
  })
  return { display, mentions }
}

export function linkedinToDisplay(stored: string): { display: string; mentions: Map<string, string> } {
  return storedToDisplay(stored, LINKEDIN_MENTION_RE)
}

export function facebookToDisplay(stored: string): { display: string; mentions: Map<string, string> } {
  return storedToDisplay(stored, FACEBOOK_MENTION_RE)
}

/**
 * Converts clean display text back to stored mention format
 * using the provided name→ID map. Replaces longest names first to avoid
 * partial matches.
 */
export function displayToStored(display: string, mentions: Map<string, string>): string {
  let result = display
  const sorted = [...mentions.entries()].sort((a, b) => b[0].length - a[0].length)
  for (const [name, id] of sorted) {
    result = result.replaceAll(`@${name}`, `@[${name}](${id})`)
  }
  return result
}

export function displayToLinkedin(display: string, mentions: Map<string, string>): string {
  return displayToStored(display, mentions)
}

export function displayToFacebook(display: string, mentions: Map<string, string>): string {
  return displayToStored(display, mentions)
}

/**
 * Calculates pixel coordinates of a given character position inside a textarea
 * by creating an off-screen mirror div with identical styling and measuring
 * the offset of a marker span placed at that position.
 */
function getCaretCoordinates(
  textarea: HTMLTextAreaElement,
  position: number,
): { top: number; left: number; lineHeight: number } {
  const mirror = document.createElement('div')
  const computed = getComputedStyle(textarea)

  mirror.style.position = 'absolute'
  mirror.style.top = '0'
  mirror.style.left = '-9999px'
  mirror.style.visibility = 'hidden'
  mirror.style.whiteSpace = 'pre-wrap'
  mirror.style.wordWrap = 'break-word'
  mirror.style.overflow = 'hidden'

  for (const prop of MIRROR_STYLE_PROPS) {
    ;(mirror.style as any)[prop] = computed[prop as any]
  }

  mirror.appendChild(document.createTextNode(textarea.value.substring(0, position)))

  const marker = document.createElement('span')
  marker.textContent = '\u200b'
  mirror.appendChild(marker)

  document.body.appendChild(mirror)

  const lh = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.2
  const coords = {
    top: marker.offsetTop - textarea.scrollTop,
    left: marker.offsetLeft - textarea.scrollLeft,
    lineHeight: lh,
  }

  document.body.removeChild(mirror)
  return coords
}

export function extractMentionQuery(text: string, cursorPos: number): { query: string; startIndex: number } | null {
  const before = text.substring(0, cursorPos)
  const atIndex = before.lastIndexOf('@')
  if (atIndex === -1) return null
  if (atIndex > 0 && !/\s/.test(text[atIndex - 1])) return null

  const query = before.substring(atIndex + 1)
  if (/\s/.test(query)) return null

  return { query, startIndex: atIndex }
}

export function insertMention(
  text: string,
  startIndex: number,
  cursorPos: number,
  username: string,
): { newText: string; newCursorPos: number } {
  const before = text.substring(0, startIndex)
  const after = text.substring(cursorPos)
  const mention = `@${username} `
  return { newText: `${before}${mention}${after}`, newCursorPos: startIndex + mention.length }
}

interface MentionableTextareaProps {
  name: string
  value: string
  onValueChange: (value: string) => void
  integrationId: number | undefined
  provider?: ExternalIntegrationEnum
  isValid?: boolean
  rows?: number
  maxHeight?: number
  disabled?: boolean
  className?: string
  inputRef?: (element: HTMLTextAreaElement | null) => void
}

export const MentionableTextarea = ({
  value,
  onValueChange,
  integrationId,
  provider,
  inputRef,
  ...inputProps
}: MentionableTextareaProps) => {
  const isLinkedIn = provider === ExternalIntegrationEnum.LinkedIn
  const isFacebook = provider === ExternalIntegrationEnum.Facebook
  const isInstagram = provider === ExternalIntegrationEnum.Instagram
  const supportsMentions = isLinkedIn || isFacebook
  const internalRef = useRef<HTMLTextAreaElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const mentionsRef = useRef<Map<string, string>>(new Map())
  const [cursorPos, setCursorPos] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [dismissed, setDismissed] = useState(false)
  const [committedQuery, setCommittedQuery] = useState('')

  const displayValue = useMemo(() => {
    if (!supportsMentions) return value
    const parser = isLinkedIn ? linkedinToDisplay : facebookToDisplay
    const { display, mentions } = parser(value)
    for (const [name, id] of mentions) {
      mentionsRef.current.set(name, id)
    }
    return display
  }, [value, supportsMentions, isLinkedIn])

  const setRefs = useCallback(
    (el: HTMLTextAreaElement | null) => {
      internalRef.current = el
      inputRef?.(el)
    },
    [inputRef],
  )

  const mentionContext = useMemo(() => extractMentionQuery(displayValue, cursorPos), [displayValue, cursorPos])

  const debouncedQuery = useDebounce(mentionContext?.query ?? '', 300)
  const searchQuery = isInstagram ? committedQuery : debouncedQuery

  const { data: users = [], isFetching, isPreviousData, isError, isAuthError } = useSearchUsers({
    integrationId,
    query: searchQuery,
  })

  const visibleUsers = useMemo(
    () => (isInstagram && isPreviousData) ? [] : users.slice(0, MAX_SUGGESTIONS),
    [isInstagram, isPreviousData, users],
  )
  const hasSearchQuery = !!mentionContext && mentionContext.query.length >= MIN_QUERY_LENGTH
  const showHint =
    integrationId &&
    !dismissed &&
    !!mentionContext &&
    (mentionContext.query.length < MIN_QUERY_LENGTH || (isInstagram && !committedQuery))
  const showDropdown =
    integrationId && !dismissed && (showHint || (hasSearchQuery && (!isInstagram || !!committedQuery)))
  const isDebouncePending = !isInstagram && (mentionContext?.query ?? '') !== debouncedQuery
  const isSearching = hasSearchQuery && !isError && (isFetching || isDebouncePending)
  const hasResults = hasSearchQuery && (!isInstagram || !!committedQuery)
  const isActive = integrationId && !dismissed && hasResults && visibleUsers.length > 0

  const dropdownPos = useMemo(() => {
    if (!showDropdown || !mentionContext || !internalRef.current) return null
    const coords = getCaretCoordinates(internalRef.current, mentionContext.startIndex)
    return { top: coords.top + coords.lineHeight + 4, left: coords.left }
  }, [showDropdown, mentionContext])

  useEffect(() => setSelectedIndex(0), [users])
  useEffect(() => {
    setDismissed(false)
    setCommittedQuery('')
  }, [mentionContext?.startIndex])

  useEffect(() => {
    if (committedQuery && mentionContext?.query !== committedQuery) {
      setCommittedQuery('')
    }
  }, [mentionContext?.query, committedQuery])

  useEffect(() => {
    const container = dropdownRef.current
    if (!container) return
    const activeOption = container.children[selectedIndex] as HTMLElement | undefined
    activeOption?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  const emitValue = useCallback(
    (displayText: string) => {
      if (supportsMentions) {
        const serializer = isLinkedIn ? displayToLinkedin : displayToFacebook
        onValueChange(serializer(displayText, mentionsRef.current))
      } else {
        onValueChange(displayText)
      }
    },
    [supportsMentions, isLinkedIn, onValueChange],
  )

  const handleSelect = useCallback(
    (user: PostTag) => {
      if (!mentionContext) return
      if (supportsMentions && user.id) {
        const id = isFacebook ? `fb:${user.id}` : user.id
        mentionsRef.current.set(user.username, id)
      }
      const { newText, newCursorPos } = insertMention(displayValue, mentionContext.startIndex, cursorPos, user.username)
      emitValue(newText)
      setDismissed(true)
      setTimeout(() => {
        internalRef.current?.focus()
        internalRef.current?.setSelectionRange(newCursorPos, newCursorPos)
        setCursorPos(newCursorPos)
      }, 0)
    },
    [mentionContext, displayValue, cursorPos, emitValue, supportsMentions, isFacebook],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (showDropdown && e.key === 'Escape') {
        e.preventDefault()
        setDismissed(true)
        return
      }

      if (
        isInstagram &&
        e.key === 'Enter' &&
        mentionContext &&
        mentionContext.query.length >= MIN_QUERY_LENGTH &&
        !committedQuery
      ) {
        e.preventDefault()
        setCommittedQuery(mentionContext.query)
        return
      }

      if (!isActive) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((i) => Math.min(i + 1, visibleUsers.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((i) => Math.max(i - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          handleSelect(visibleUsers[selectedIndex])
          break
      }
    },
    [showDropdown, isActive, visibleUsers, selectedIndex, handleSelect, isInstagram, mentionContext, committedQuery],
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setCursorPos((e.target as HTMLTextAreaElement).selectionStart ?? 0)
      emitValue(e.target.value)
    },
    [emitValue],
  )

  const handleCursorChange = useCallback(() => {
    if (internalRef.current) {
      setCursorPos(internalRef.current.selectionStart ?? 0)
    }
  }, [])

  const mentionHintText = () => {
    if (isInstagram) {
      return mentionContext && mentionContext.query.length >= MIN_QUERY_LENGTH
        ? 'Press Enter to search'
        : 'Type a username and press Enter'
    }
    if (isLinkedIn) return 'Mention a page follower'
    if (isFacebook) return 'Mention a page'
    return 'Search a user'
  }

  return (
    <div className={Styles.mentionableContainer} onKeyUp={handleCursorChange} onClick={handleCursorChange}>
      <Input
        {...inputProps}
        value={displayValue}
        isTextarea
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        ref={setRefs}
      />
      {showDropdown && dropdownPos && (
        <div
          ref={dropdownRef}
          className={Styles.mentionDropdown}
          role="listbox"
          style={{ top: dropdownPos.top, left: dropdownPos.left }}
        >
          {showHint && (
            <div className={Styles.mentionMessage}>{mentionHintText()}</div>
          )}
          {hasResults && isAuthError && (
            <div className={Styles.mentionMessage}>Account disconnected. Please reconnect to search users.</div>
          )}
          {hasResults && isError && !isAuthError && (
            <div className={Styles.mentionMessage}>Unable to search users. Please try again.</div>
          )}
          {hasResults && visibleUsers.length === 0 && isSearching && (
            <div className={Styles.mentionMessage}>Searching…</div>
          )}
          {hasResults && visibleUsers.length === 0 && !isSearching && !isError && (
            <div className={Styles.mentionMessage}>No results found</div>
          )}
          {hasResults &&
            visibleUsers.map((user, index) => (
              <div
                key={user.id}
                role="option"
                aria-selected={index === selectedIndex}
                className={cx(Styles.mentionOption, {
                  [Styles.mentionOptionActive]: index === selectedIndex,
                })}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelect(user)
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <Avatar
                  size={20}
                  label={getInitials(user.name || user.username)}
                  url={user.profile_image_url || null}
                />
                <span className={Styles.mentionUsername}>{supportsMentions ? user.username : `@${user.username}`}</span>
                {user.name && <span className={Styles.mentionName}>{user.name}</span>}
              </div>
            ))}
        </div>
      )}
    </div>
  )
}
